import currency from "currency.js";
import { format } from "date-fns";
import { FormikHelpers } from "formik";
import { find, startCase } from "lodash";
import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import notifications from "@/app/container/notifications";
import { useAppContext } from "@/app/context";
import { useActiveRegistryDisplayName } from "@/app/hooks/use-active-registry-display-name";
import { useAllFeatureToggleValues } from "@/app/hooks/use-feature-toggle/use-all-feature-toggle-values";
import { useTransactionFee } from "@/app/hooks/use-transaction-fee";
import { AppDateFormats, formatDate } from "@/app/lib/format-date";
import { getRegistryCodeForTransactionFee, SupportedLinkedRegistry } from "@/app/lib/transaction-fee-helpers";
import {
  MarketTradeContractsDocument,
  TradeContractAction,
  TradePosition,
  useFewAccountDetailsQuery,
  useReviewTradeContractMutation,
  useTenantsQuery,
} from "@/app/types/generated/graphql";
import { AggregatedRegistryCode, ANY_REGISTRY_CODE } from "@/app/types/okta.types";

import { OrderLifeCycle } from "../common";
import { getProductAttributesAsDisplayAttributes } from "../helpers";
import {
  AcceptForwardTradeFormValues,
  UseAcceptForwardTradeOrderModal,
} from "./accept-forward-trade-order-modal.types";

export const useAcceptForwardTradeOrderModal: UseAcceptForwardTradeOrderModal = ({ closeModal, tradeContract }) => {
  const { t } = useTranslation();
  const [currentStep, setCurrentStep] = useState(OrderLifeCycle.Form);
  const { data: tenantsData } = useTenantsQuery();
  const activeRegistryCode = tradeContract.registryCode as AggregatedRegistryCode;
  const { userAccountInfo } = useAppContext();

  const { activeRegistryDisplayName, registriesQueryLoading } = useActiveRegistryDisplayName(activeRegistryCode);
  const { data: accountData } = useFewAccountDetailsQuery();
  const {
    toggles: { enableCrossRegistryForwardTrading },
    loading: featureTogglesLoading,
  } = useAllFeatureToggleValues();

  const getTransactionFeeRegistryCode = () => {
    if (!enableCrossRegistryForwardTrading || activeRegistryCode !== ANY_REGISTRY_CODE) {
      return activeRegistryCode;
    }

    return (
      getRegistryCodeForTransactionFee(userAccountInfo?.linkedRegistries as SupportedLinkedRegistry[]) ??
      activeRegistryCode
    );
  };

  const { getAppliedFee, loading } = useTransactionFee({ registryCode: getTransactionFeeRegistryCode() });

  const [currencySymbol] = useMemo(() => {
    const tenant = find(tenantsData?.tenants, { id: accountData?.account?.tenantId });
    return [tenant?.localisation?.currencySymbol ?? "$"];
  }, [accountData?.account?.tenantId, tenantsData]);

  const tradePosition = tradeContract?.position === TradePosition.Bid ? TradePosition.Ask : TradePosition.Bid;
  const isPositionBid = tradePosition === TradePosition.Bid;
  const fee = useMemo(() => getAppliedFee(tradePosition), [getAppliedFee, tradePosition]);

  const [acceptTradeContract] = useReviewTradeContractMutation();

  const handleSubmit = useCallback(
    async (_, actions: FormikHelpers<AcceptForwardTradeFormValues>) => {
      try {
        await acceptTradeContract({
          variables: {
            input: {
              action: TradeContractAction.Accept,
              id: tradeContract.id,
              registryCode: activeRegistryCode,
            },
          },
          refetchQueries: [MarketTradeContractsDocument],
        });
        setCurrentStep(OrderLifeCycle.Success);
      } catch (e) {
        e instanceof Error &&
          notifications.error({
            description: t(e?.message),
          });
      } finally {
        actions.setSubmitting(false);
      }
    },
    [acceptTradeContract, t, tradeContract.id, activeRegistryCode],
  );

  const tradeAttributes = useMemo(
    () => [
      {
        label: "Order Lodgement Date",
        value: format(new Date(tradeContract.createdDate), AppDateFormats.AbbreviatedMonthFormat),
      },
      {
        label: "Trade Settlement Date",
        value: formatDate(tradeContract.tradeSettlementDate, {
          formatStyle: AppDateFormats.AbbreviatedMonthFormat,
          noOffset: true,
          noTZ: true,
        }),
      },
      {
        label: "Type",
        value: startCase(tradeContract.position.toLowerCase()),
      },
      {
        label: "Quantity",
        value: tradeContract.volume.toLocaleString(),
      },
      {
        label: "Price per unit",
        value: currency(tradeContract.unitPrice, { fromCents: true }).format(),
      },
    ],
    [tradeContract],
  );

  const productAttributes = useMemo(() => {
    return getProductAttributesAsDisplayAttributes(t, {
      attributes: tradeContract.attributes,
      formattedAttributes: tradeContract.formattedAttributes,
      activeRegistryCode,
      activeRegistryDisplayName,
    });
  }, [t, tradeContract, activeRegistryCode, activeRegistryDisplayName]);

  return {
    currencySymbol,
    currentStep,
    loading: loading || registriesQueryLoading || featureTogglesLoading,
    fee,
    isPositionBid,
    tradePosition,
    tradeContract,
    tradeAttributes,
    productAttributes,
    activeRegistryDisplayName,
    handleSubmit,
    closeModal,
  };
};
